/**
 * useQuizSession - Firebase Session Listener Hook
 * 
 * Manages all Firebase listeners for quiz session data:
 * - Session metadata (questions, settings)
 * - Current question state
 * - Answer reveal state
 * - Participants list
 * - Timer state
 * 
 * Uses unifiedDataService for all RTDB operations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { LiveSessionService, RealtimeDBService } from '../../../firebase/unifiedDataService';
import { normalizeQuestion } from '../../../utils/llm-generator/questionTransformers';
import { Logger } from '../../../utils/logger';

const log = Logger.create('useQuizSession');

/**
 * Custom hook for Firebase quiz session listeners
 * @param {string} sessionId - The quiz session ID (can be short code or full ID)
 * @returns {Object} Session data and state
 */
export function useQuizSession(sessionId) {
  // Resolved instance path (full path including userId)
  const [resolvedInstancePath, setResolvedInstancePath] = useState(null);
  const [resolvedInstanceId, setResolvedInstanceId] = useState(null);

  // Session data
  const [questions, setQuestions] = useState(null);
  const [settings, setSettings] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizStatus, setQuizStatus] = useState('active'); // 'active', 'completed', etc.
  
  // Template info (for student stats)
  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState(null);
  
  // Quiz state
  const [showResults, setShowResults] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [answersLocked, setAnswersLocked] = useState(false);
  
  // Timer state from server
  const [timerState, setTimerState] = useState(null);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  
  // Participants
  const [participants, setParticipants] = useState({});
  const [currentAnswers, setCurrentAnswers] = useState({});
  
  // Loading/error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Refs for cleanup
  const listenersRef = useRef([]);

  // Resolve short code to instance path (quiz_instances/{userId}/{instanceId})
  useEffect(() => {
    if (!sessionId) return;

    // Check if sessionId is a short code (typically 6 chars) or a full Firebase ID
    const isShortCode = sessionId.length <= 8 && !sessionId.startsWith('-');

    if (isShortCode) {
      // Look up in session_codes using LiveSessionService
      LiveSessionService.getByCode(sessionId).then((data) => {
        if (data) {
          // Build full path: quiz_instances/{userId}/{instanceId}
          const fullPath = `quiz_instances/${data.userId}/${data.instanceId}`;
          setResolvedInstancePath(fullPath);
          setResolvedInstanceId(data.instanceId);
          log.debug(`Resolved session code ${sessionId} to path: ${fullPath}`);
        } else {
          // Try legacy sessions path as fallback
          setResolvedInstancePath(`sessions/${sessionId}`);
          setResolvedInstanceId(sessionId);
          log.warn(`Session code ${sessionId} not found, trying legacy path`);
        }
      }).catch((err) => {
        log.error('Error resolving session code:', err);
        setResolvedInstancePath(`sessions/${sessionId}`);
        setResolvedInstanceId(sessionId);
      });
    } else {
      // Full ID provided - try legacy sessions path
      setResolvedInstancePath(`sessions/${sessionId}`);
      setResolvedInstanceId(sessionId);
    }
  }, [sessionId]);

  // Get server time offset for accurate timer
  useEffect(() => {
    const unsubscribe = RealtimeDBService.subscribeServerTimeOffset((offset) => {
      setServerTimeOffset(offset);
    });

    return () => unsubscribe && unsubscribe();
  }, []);

  // Set up Firebase listeners once we have the resolved instance path
  useEffect(() => {
    if (!resolvedInstancePath) return;

    // Single ROOT listener for all quiz state using RealtimeDBService
    const unsubscribe = RealtimeDBService.subscribe(resolvedInstancePath, (data) => {
      if (data) {
        // Update questions if present (only on initial load or major changes)
        // Normalize questions to ensure correctAnswerIndices is always an array of numbers
        if (data.questions) {
          const questionsArray = Array.isArray(data.questions) 
            ? data.questions 
            : Object.values(data.questions);
          const normalizedQuestions = questionsArray.map(q => normalizeQuestion(q));
          setQuestions(prev => {
            // Only update if questions actually changed
            // Handle both first load (prev is null) and rejoin scenarios
            if (!prev || prev.length !== normalizedQuestions.length) return normalizedQuestions;
            return prev;
          });
        }
        
        // Update settings if present
        if (data.settings) {
          setSettings(data.settings);
        }
        
        // Update template info (for student stats recording)
        if (data.templateId) {
          setTemplateId(data.templateId);
        }
        if (data.name) {
          setTemplateName(data.name);
        }
        
        // Update quiz CONTROL state only (these are admin-controlled)
        if (data.currentQuestionIndex !== undefined) {
          setCurrentQuestionIndex(data.currentQuestionIndex);
        }
        if (data.showResults !== undefined) {
          setShowResults(data.showResults === true);
        }
        if (data.showCorrectAnswer !== undefined) {
          setShowCorrectAnswer(data.showCorrectAnswer === true);
        }
        if (data.answersLocked !== undefined) {
          setAnswersLocked(data.answersLocked === true);
        }
        if (data.timerState !== undefined) {
          setTimerState(data.timerState);
        }
        
        // Update quiz status (active, completed, etc.)
        if (data.status !== undefined) {
          setQuizStatus(data.status);
        }
        
        // NOTE: participants and answers are NOT updated here to avoid
        // re-render storms when other participants submit answers
        // They are fetched on-demand by jokers when needed
        
      } else {
        log.warn(`No data at path: ${resolvedInstancePath}`);
        setError('Quiz-Daten nicht gefunden');
      }
    });

    // Store unsubscribe for cleanup
    listenersRef.current.push({ unsubscribe });

    // Mark data as loaded after initial fetch
    setLoading(false);
    setDataLoaded(true);

    // Cleanup all listeners on unmount
    return () => {
      listenersRef.current.forEach(({ unsubscribe }) => {
        if (unsubscribe) unsubscribe();
      });
      listenersRef.current = [];
    };
  }, [resolvedInstancePath]);

  /**
   * Check if a participant exists in the session
   */
  const participantExists = useCallback((participantId) => {
    return !!participants[participantId];
  }, [participants]);

  /**
   * Get participant count
   */
  const participantCount = Object.keys(participants).length;

  /**
   * Get current question
   */
  const currentQuestion = questions?.[currentQuestionIndex] || null;

  /**
   * Check if quiz is in intro mode (waiting to start)
   */
  const isInIntroMode = settings?.showIntroScreen && 
    (currentQuestionIndex === -1 || currentQuestionIndex === undefined || currentQuestionIndex === null);

  /**
   * Check if answering is locked (manually locked OR answer revealed + lock setting enabled)
   */
  const isAnsweringLocked = answersLocked || 
    (showCorrectAnswer && (settings?.lockOnReveal ?? true));

  return {
    // Session Info
    sessionId: resolvedInstanceId || sessionId,
    resolvedInstanceId,
    instancePath: resolvedInstancePath, // Full Firebase path for writes

    // Quiz Data
    questions: questions || [],
    settings: settings || {},
    currentQuestionIndex,
    currentQuestion,
    totalQuestions: questions?.length || 0,
    
    // Template info (for student stats)
    templateId,
    templateName,

    // Quiz State
    showResults,
    showCorrectAnswer,
    answersLocked,
    isAnsweringLocked,
    isInIntroMode,
    quizStatus, // 'active', 'completed', etc.

    // Timer
    timerState,
    serverTimeOffset,

    // Participants
    participants: participants || {},
    participantCount,
    participantExists,
    currentAnswers,

    // Loading State
    loading,
    error,
    dataLoaded,

    // Settings shortcuts
    quizDisplayName: settings?.displayName || sessionId,
    enableGamification: settings?.enableGamification ?? false,
    anonymousMode: settings?.anonymousMode ?? false,
    showStreak: settings?.showStreak ?? true,
    enableJokers: settings?.enableJokers ?? false,
  };
}

export default useQuizSession;
